import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Trash2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import * as api from '@/lib/api';

// Cover image attached to a vault account: used as thumbnail for clips posted with that account
const AuthImage = ({ path, alt, className, ...rest }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let cancelled = false;
    api.fetchFileBlobUrl(path).then((u) => { if (cancelled) URL.revokeObjectURL(u); else { url = u; setSrc(u); } }).catch(() => {});
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
  return <img src={src} alt={alt} className={className} {...rest} />;
};

export const AccountCover = ({ credential, onChanged }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const coverPath = credential.cover_image?.path;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadAccountCover(credential.id, file);
      toast.success('Capa salva! Os cortes desta conta usarão esta imagem.');
      onChanged?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao enviar capa');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.removeAccountCover(credential.id);
      toast.success('Capa removida. O app escolherá o melhor frame de cada corte.');
      onChanged?.();
    } catch {
      toast.error('Falha ao remover capa');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`account-cover-${credential.id}`}>
      <div className="w-12 h-[72px] rounded-md overflow-hidden bg-muted border border-border flex items-center justify-center shrink-0">
        {coverPath ? (
          <AuthImage path={coverPath} alt="Capa" className="w-full h-full object-cover" data-testid="account-cover-image" />
        ) : (
          <Sparkles className="w-4 h-4 text-muted-foreground" title="Capa automática" />
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">
          {coverPath ? 'Capa personalizada' : 'Sem capa: o app escolhe o frame'}
        </span>
        <div className="flex gap-1">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="account-cover-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="account-cover-upload-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
            <span className="ml-1 text-xs">{coverPath ? 'Trocar' : 'Adicionar capa'}</span>
          </Button>
          {coverPath && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={remove} data-testid="account-cover-remove-btn">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const AccountStatusBadge = ({ status }) => {
  const map = {
    ok: ['bg-emerald-500/15 text-emerald-400', 'Verificada pelo Agente'],
    needs_login: ['bg-amber-500/15 text-amber-400', 'Precisa de login'],
    needs_2fa: ['bg-amber-500/15 text-amber-400', 'Precisa de 2FA'],
    blocked: ['bg-red-500/15 text-red-400', 'Bloqueada'],
    suspended: ['bg-red-500/15 text-red-400', 'Suspensa'],
    not_found: ['bg-red-500/15 text-red-400', 'Não encontrada'],
  };
  const [cls, label] = map[status] || ['bg-muted text-muted-foreground', 'Aguardando verificação do Agente'];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid="account-status-badge">{label}</span>;
};
